import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DownloadSimple, FunnelSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Phase 5 — Staff Reports. Single page covers all 10 report types with a
// type selector + universal filters (date range / staff / status / month).
// Server returns { columns: [], rows: [] } so this stays report-agnostic.

const REPORTS = [
  { key: 'daily-attendance',   label: 'Daily Attendance',     filters: ['from', 'to', 'staff', 'status'] },
  { key: 'monthly-attendance', label: 'Monthly Attendance',   filters: ['month', 'staff'] },
  { key: 'staff-attendance',   label: 'Staff-wise Attendance', filters: ['from', 'to', 'staff'] },
  { key: 'leave',              label: 'Leave Report',         filters: ['from', 'to', 'staff', 'status'] },
  { key: 'salary',             label: 'Salary Report',        filters: ['month', 'staff', 'status'] },
  { key: 'productivity',       label: 'Productivity Report',  filters: ['month', 'staff'] },
  { key: 'staff-revenue',      label: 'Staff-wise Revenue',   filters: ['month', 'staff'] },
  { key: 'late-coming',        label: 'Late Coming Report',   filters: ['from', 'to', 'staff'] },
  { key: 'absent',             label: 'Absent Report',        filters: ['from', 'to', 'staff'] },
  { key: 'performance',        label: 'Performance Report',   filters: ['month', 'staff'] },
];

const STATUS_OPTIONS = {
  'daily-attendance':   ['present', 'absent', 'half_day', 'leave', 'weekly_off'],
  'leave':              ['pending', 'approved', 'rejected'],
  'salary':             ['pending', 'paid'],
};

const downloadCSV = (filename, columns, rows) => {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.join(','), ...rows.map(r => columns.map(c => esc(r[c])).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

export default function StaffReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [reportKey, setReportKey] = useState('monthly-attendance');
  const meta = REPORTS.find(r => r.key === reportKey) || REPORTS[0];
  const [staff, setStaff] = useState([]);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [fromD, setFromD] = useState('');
  const [toD, setToD] = useState('');
  const [userId, setUserId] = useState('');
  const [status, setStatus] = useState('');
  const [data, setData] = useState({ columns: [], rows: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAdmin) api.get('/users').then(r => setStaff(r.data.filter(u => u.active !== false))).catch(() => {});
  }, [isAdmin]);

  const run = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (meta.filters.includes('month') && month) params.set('month', month);
    if (meta.filters.includes('from') && fromD) params.set('from', fromD);
    if (meta.filters.includes('to') && toD) params.set('to', toD);
    if (meta.filters.includes('staff') && userId) params.set('user_id', userId);
    if (meta.filters.includes('status') && status) params.set('status', status);
    api.get(`/staff-reports/${reportKey}?${params.toString()}`)
      .then(r => setData(r.data))
      .catch(err => { toast.error(err.response?.data?.detail || 'Failed to load'); setData({ columns: [], rows: [] }); })
      .finally(() => setLoading(false));
  }, [reportKey, month, fromD, toD, userId, status, meta.filters]);

  useEffect(() => { run(); }, [run]);

  const exportCsv = () => {
    if (!data.rows.length) { toast.error('Nothing to export'); return; }
    const fname = `${reportKey}-${month || new Date().toISOString().slice(0, 10)}.csv`;
    downloadCSV(fname, data.columns, data.rows);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="staff-reports-page">
      <p className="text-xs text-[#52525B]">
        {isAdmin ? 'Run any of the 10 staff reports. Filters adapt to the report type.' : 'Reports limited to your own data.'}
      </p>

      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Report</Label>
              <Select value={reportKey} onValueChange={setReportKey}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="report-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REPORTS.map(r => (<SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {meta.filters.includes('month') && (
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Month</Label>
                <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="rounded-lg mt-1" data-testid="report-month" />
              </div>
            )}
            {meta.filters.includes('from') && (
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">From</Label>
                <Input type="date" value={fromD} onChange={e => setFromD(e.target.value)} className="rounded-lg mt-1" data-testid="report-from" />
              </div>
            )}
            {meta.filters.includes('to') && (
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">To</Label>
                <Input type="date" value={toD} onChange={e => setToD(e.target.value)} className="rounded-lg mt-1" data-testid="report-to" />
              </div>
            )}
            {meta.filters.includes('staff') && isAdmin && (
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Staff</Label>
                <Select value={userId || 'all'} onValueChange={v => setUserId(v === 'all' ? '' : v)}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="report-staff"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    {staff.map(s => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {meta.filters.includes('status') && STATUS_OPTIONS[reportKey] && (
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Status</Label>
                <Select value={status || 'all'} onValueChange={v => setStatus(v === 'all' ? '' : v)}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="report-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    {STATUS_OPTIONS[reportKey].map(s => (<SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center pt-2 border-t border-[#F4F4F5]">
            <p className="text-xs text-[#52525B] flex items-center gap-1.5">
              <FunnelSimple size={12} /> {data.rows.length} row{data.rows.length === 1 ? '' : 's'}
            </p>
            <Button onClick={exportCsv} disabled={!data.rows.length} className="bg-[#10B981] text-white hover:bg-[#059669] hover:text-white rounded-xl" data-testid="export-csv-btn">
              <DownloadSimple size={16} className="mr-1.5" /> Download CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-0">
          {loading ? <p className="p-6 text-sm text-[#52525B] text-center">Loading…</p> : data.rows.length === 0 ? (
            <p className="p-8 text-sm text-[#52525B] text-center">No data for the chosen filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E4E4E7]">
                    {data.columns.map(c => (
                      <TableHead key={c} className="text-[10px] uppercase tracking-[0.12em] font-semibold whitespace-nowrap">{c.replace(/_/g, ' ')}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.map((row, idx) => (
                    <TableRow key={idx} className="border-[#E4E4E7]" data-testid={`report-row-${idx}`}>
                      {data.columns.map(c => (
                        <TableCell key={c} className="text-xs whitespace-nowrap">{typeof row[c] === 'boolean' ? (row[c] ? '✓' : '—') : (row[c] ?? '—')}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
